import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/lt';
import en from './locales/en';
import lt from './locales/lt';

export const LANGUAGES = [
    { code: 'en', short: 'EN', label: 'English', native: 'English' },
    { code: 'lt', short: 'LT', label: 'Lithuanian', native: 'Lietuvių' }
];

export const SHOW_LANGUAGE_SWITCH = true;

const STATUS_KEYS = {
    Created: 'status.created',
    Accepted: 'status.accepted',
    Started: 'status.started',
    WIP: 'status.wip',
    Completed: 'status.completed'
};

const dictionaries = { en, lt };
const STORAGE_KEY = 'ipsum.language';

const envDefault = String(process.env.REACT_APP_DEFAULT_LANGUAGE || 'en').toLowerCase();

function lookup(dict, path) {
    return path.split('.').reduce((acc, key) => (
        acc && acc[key] !== undefined && acc[key] !== null ? acc[key] : undefined
    ), dict);
}

function interpolate(value, vars) {
    if (vars == null || typeof value !== 'string') return value;
    return value.replace(/\{\{(\w+)\}\}/g, (_, key) => (
        vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : ''
    ));
}

export function isSupportedLanguage(code) {
    return Boolean(code && dictionaries[code]);
}

export function detectLanguage() {
    if (!SHOW_LANGUAGE_SWITCH) return 'en';
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (isSupportedLanguage(saved)) return saved;
    } catch (err) { /* keep default */ }

    if (typeof navigator !== 'undefined') {
        const nav = String(navigator.language || navigator.userLanguage || '').toLowerCase();
        if (nav.startsWith('lt')) return 'lt';
        if (nav.startsWith('en')) return 'en';
    }

    return isSupportedLanguage(envDefault) ? envDefault : 'en';
}

export function translate(language, key, vars) {
    const primary = lookup(dictionaries[language] || {}, key);
    const fallback = language !== 'en' ? lookup(dictionaries.en, key) : undefined;
    const value = primary !== undefined ? primary : fallback;
    if (value === undefined) return key;
    return interpolate(value, vars);
}

function applyDocumentLanguage(code) {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = code;
    try {
        moment.locale(code === 'lt' ? 'lt' : 'en');
    } catch (err) { /* ignore */ }
}

export function translateStatus(language, status) {
    const key = STATUS_KEYS[status];
    return key ? translate(language, key) : (status || '');
}

export function translateCatalog(language, value) {
    const raw = String(value == null ? '' : value).trim();
    if (!raw) return value || '';
    const dict = (dictionaries[language] || {}).catalog || {};
    if (dict[raw] != null) return dict[raw];
    if (dict[value] != null) return dict[value];
    const lower = raw.toLowerCase();
    const match = Object.keys(dict).find((key) => key.toLowerCase() === lower);
    if (match) return dict[match];
    if (language !== 'en') return translateCatalog('en', value);
    return value;
}

const I18nContext = createContext({
    language: 'en',
    languages: LANGUAGES,
    setLanguage: () => {},
    t: (key, vars) => translate('en', key, vars),
    tStatus: (status) => translateStatus('en', status),
    tCatalog: (value) => translateCatalog('en', value)
});

export function I18nProvider({ children }) {
    const [language, setLanguageState] = useState(detectLanguage);

    useEffect(() => {
        applyDocumentLanguage(language);
    }, [language]);

    const setLanguage = useCallback((code) => {
        if (!isSupportedLanguage(code)) return;
        setLanguageState(code);
        try { window.localStorage.setItem(STORAGE_KEY, code); } catch (err) { /* ignore */ }
        applyDocumentLanguage(code);
    }, []);

    const t = useCallback((key, vars) => translate(language, key, vars), [language]);
    const tStatus = useCallback((status) => translateStatus(language, status), [language]);
    const tCatalog = useCallback((value) => translateCatalog(language, value), [language]);

    const value = useMemo(() => ({
        language,
        languages: LANGUAGES,
        setLanguage,
        t,
        tStatus,
        tCatalog
    }), [language, setLanguage, t, tStatus, tCatalog]);

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    return useContext(I18nContext);
}

export default I18nContext;
